import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { Button } from "../../../../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components/ui/popover";

// Options are id+name pairs; ids may be numbers (server-backed filters like
// process/owner on the select step) or strings (client-side filters like the
// review step's process names).
export interface FilterComboboxOption<Id extends number | string> {
  id: Id;
  name: string;
}

interface FilterComboboxProps<Id extends number | string> {
  options: FilterComboboxOption<Id>[];
  value: Id | null;
  onChange: (id: Id | null) => void;
  allLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  disabled?: boolean;
  className?: string;
}

// Searchable single-select filter for the Optro import select step (used for
// both process and owner): a plain shadcn `Select` doesn't scale to long
// option lists, so this builds a combobox on the
// same `Popover` + `Command` primitives as `ControlSearchCombobox`, filtering
// the (already-fetched) option list client-side via `Command`'s built-in
// search instead of a server round trip.
export function FilterCombobox<Id extends number | string>({
  options,
  value,
  onChange,
  allLabel,
  searchPlaceholder,
  emptyMessage,
  disabled,
  className,
}: FilterComboboxProps<Id>) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value],
  );

  const handleSelect = (id: Id) => {
    onChange(id === value ? null : id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-48 justify-between font-normal overflow-hidden shrink-0",
            !selected && "text-stone-500",
            className,
          )}
        >
          <span className="truncate text-left">
            {selected ? selected.name : allLabel}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {selected && (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            {options.length === 0 ? (
              <CommandEmpty className="text-sm text-center py-2 px-4">
                {emptyMessage}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((option) => (
                  // cmdk identifies items by `value`, so it must be unique per
                  // option: distinct users can share a display name (deleted
                  // users especially), and same-name items would otherwise
                  // highlight and select together. Search matches `keywords`.
                  <CommandItem
                    key={option.id}
                    value={String(option.id)}
                    keywords={[option.name]}
                    onSelect={() => handleSelect(option.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
